import { useState, useEffect } from 'react'
import { Search, User, ChevronRight } from 'lucide-react'
import { fetchUsers, fetchEmulatedUser } from '../../services/admin'
import type { AdminUser, EmulatedUser } from '../../services/admin'

export default function EmulatePanel() {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [emulated, setEmulated] = useState<EmulatedUser | null>(null)
  const [emulateLoading, setEmulateLoading] = useState(false)
  const [expandedJob, setExpandedJob] = useState<string | null>(null)

  useEffect(() => {
    fetchUsers()
      .then(setUsers)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  async function emulateUser(userId: string) {
    setEmulateLoading(true)
    try {
      const data = await fetchEmulatedUser(userId)
      setEmulated(data)
      setExpandedJob(null)
    } catch {
      // Error handled
    } finally {
      setEmulateLoading(false)
    }
  }

  const filtered = users.filter(u =>
    !search || u.email.toLowerCase().includes(search.toLowerCase()) ||
    (u.name && u.name.toLowerCase().includes(search.toLowerCase()))
  )

  if (emulateLoading) {
    return <p className="font-mono text-sm text-slate-500 animate-pulse">Loading user data...</p>
  }

  // Emulated user view
  if (emulated) {
    const { user, quota, jobs, machineProfiles, toolLibraries } = emulated

    return (
      <div className="space-y-6">
        {/* Back */}
        <button
          onClick={() => setEmulated(null)}
          className="font-mono text-xs text-slate-400 hover:text-cyan-400 transition-colors"
        >
          &larr; Back to user list
        </button>

        {/* User header */}
        <div className="glass rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-full bg-cyan-500/15 flex items-center justify-center">
              <User className="w-4 h-4 text-cyan-400" />
            </div>
            <div>
              <p className="font-mono text-sm text-slate-200">{user.email}</p>
              {user.name && <p className="font-mono text-xs text-slate-500">{user.name}</p>}
            </div>
          </div>
          <div className="flex gap-4 mt-3">
            <span className="font-mono text-xs text-slate-400">
              Plan: <span className="text-slate-200">{quota?.plan ?? 'none'}</span>
            </span>
            <span className="font-mono text-xs text-slate-400">
              Limit: <span className="text-slate-200">{quota?.jobs_per_month ?? 0}/mo</span>
            </span>
            <span className="font-mono text-xs text-slate-400">
              Joined: <span className="text-slate-200">{new Date(user.created_at).toLocaleDateString()}</span>
            </span>
          </div>
        </div>

        {/* Jobs */}
        <div>
          <h3 className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-3">
            Jobs ({jobs.length})
          </h3>
          {jobs.length === 0 ? (
            <p className="font-mono text-xs text-slate-600">No jobs</p>
          ) : (
            <div className="space-y-2">
              {jobs.map(job => {
                const jobId = job.id as string
                const isExpanded = expandedJob === jobId
                const messages = (job.messages ?? []) as Array<{ id: string; role: string; content: string; created_at: string }>

                return (
                  <div key={jobId} className="glass rounded-xl overflow-hidden">
                    <button
                      onClick={() => setExpandedJob(isExpanded ? null : jobId)}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.03] transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <ChevronRight className={`w-3.5 h-3.5 text-slate-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        <span className="font-mono text-xs text-slate-200">{job.label as string}</span>
                        <span className="font-mono text-[10px] text-slate-500">{job.material as string}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-[10px] text-slate-600">{messages.length} msgs</span>
                        <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full ${job.closed_at ? 'text-slate-500 bg-white/5' : 'text-emerald-400 bg-emerald-400/10'}`}>
                          {job.closed_at ? 'closed' : 'open'}
                        </span>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="border-t border-cyan-500/10 px-4 py-3 space-y-2 max-h-96 overflow-y-auto">
                        {messages.length === 0 ? (
                          <p className="font-mono text-xs text-slate-600">No messages</p>
                        ) : (
                          messages.map(msg => (
                            <div key={msg.id} className="flex gap-2">
                              <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded shrink-0 ${
                                msg.role === 'user' ? 'text-cyan-400 bg-cyan-400/10' : 'text-slate-400 bg-white/5'
                              }`}>
                                {msg.role}
                              </span>
                              <p className="font-mono text-xs text-slate-300 whitespace-pre-wrap line-clamp-4">
                                {msg.content}
                              </p>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Machine profiles */}
        <div>
          <h3 className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-3">
            Machine Profiles ({machineProfiles.length})
          </h3>
          {machineProfiles.length === 0 ? (
            <p className="font-mono text-xs text-slate-600">No profiles</p>
          ) : (
            <div className="glass rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-cyan-500/10">
                    <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Name</th>
                    <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Controller</th>
                    <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {machineProfiles.map((p, i) => (
                    <tr key={i} className="border-b border-white/[0.06] last:border-0">
                      <td className="px-3 py-2 font-mono text-xs text-slate-200">{p.name as string}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-400">{p.controller as string}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-400">{p.type as string}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Tool libraries */}
        <div>
          <h3 className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-3">
            Tool Libraries ({toolLibraries.length})
          </h3>
          {toolLibraries.length === 0 ? (
            <p className="font-mono text-xs text-slate-600">No tool libraries</p>
          ) : (
            <div className="glass rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-cyan-500/10">
                    <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Name</th>
                    <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {toolLibraries.map((lib, i) => (
                    <tr key={i} className="border-b border-white/[0.06] last:border-0">
                      <td className="px-3 py-2 font-mono text-xs text-slate-200">{lib.name as string}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-400">{lib.description as string}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    )
  }

  // User selection view
  return (
    <div className="space-y-4">
      <p className="font-mono text-xs text-slate-500">
        Select a user to view their full account data (jobs, messages, profiles, tools).
      </p>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by email or name..."
          className="w-full pl-10 pr-3 py-2 bg-white/5 border border-cyan-500/20 rounded-lg font-mono text-xs text-slate-200 placeholder:text-slate-600 focus:border-cyan-400 outline-none"
        />
      </div>

      {loading ? (
        <p className="font-mono text-xs text-slate-500 animate-pulse">Loading users...</p>
      ) : (
        <div className="glass rounded-xl overflow-hidden">
          {filtered.length === 0 ? (
            <p className="font-mono text-xs text-slate-600 px-3 py-4 text-center">No users found</p>
          ) : (
            <div className="divide-y divide-white/[0.06]">
              {filtered.map(user => (
                <button
                  key={user.id}
                  onClick={() => emulateUser(user.id)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.03] transition-colors text-left"
                >
                  <div>
                    <span className="font-mono text-xs text-slate-200">{user.email}</span>
                    {user.name && <span className="font-mono text-[10px] text-slate-500 ml-2">{user.name}</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full ${
                      user.plan === 'pro' ? 'text-cyan-400 bg-cyan-400/10' :
                      user.plan === 'admin' ? 'text-amber-400 bg-amber-400/10' :
                      'text-slate-400 bg-white/5'
                    }`}>
                      {user.plan}
                    </span>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
